/**
 * Generic grid data fetcher - works with any Flow Builder grid
 * Does not expose what type of data it's fetching (departments, agencies, permits, etc.)
 */
import { get } from '../components/library/api/rest/index';
import { unwrapXmlStyleContent } from '../utils/apiFieldText';
function normalizeRow(row) {
    if (!row || typeof row !== 'object')
        return {};
    const out = {};
    for (const [k, v] of Object.entries(row)) {
        out[k.toLowerCase()] = v;
    }
    return out;
}
function readString(norm, keys) {
    for (const key of keys) {
        const v = norm[key.toLowerCase()];
        if (v == null)
            continue;
        if (typeof v === 'string' && v.trim())
            return unwrapXmlStyleContent(v);
        if (typeof v === 'number' && Number.isFinite(v))
            return String(v);
    }
    return '';
}
/**
 * Resolve asset URLs - handles both relative and absolute URLs
 */
export function resolveAssetUrl(baseUrl, raw) {
    if (!raw || !raw.trim())
        return undefined;
    const base = baseUrl.replace(/\/$/, '');
    const t = raw.trim();
    if (/^https?:\/\//i.test(t)) {
        const b = base.toLowerCase();
        if (t.toLowerCase().startsWith(b))
            return t;
        return undefined;
    }
    const path = t.startsWith('/') ? t : `/${t}`;
    return `${base}${path}`;
}
/**
 * Generic grid row mapper - transforms API response to standard format
 */
function mapRowToGridItem(row, config, baseUrl) {
    const norm = normalizeRow(row);
    // Extract fields using config mappings
    const recordId = readString(norm, config.fieldMappings.recordId);
    if (!recordId)
        return null;
    let title = readString(norm, config.fieldMappings.title);
    if (!title.trim())
        return null;
    const subtitle = config.fieldMappings.subtitle
        ? readString(norm, config.fieldMappings.subtitle) || undefined
        : undefined;
    const icon = config.fieldMappings.icon
        ? readString(norm, config.fieldMappings.icon) || undefined
        : undefined;
    const accentHex = config.fieldMappings.accentColor
        ? readString(norm, config.fieldMappings.accentColor)
        : '';
    const accentColor = accentHex.startsWith('#') ? accentHex : undefined;
    const image = config.fieldMappings.image
        ? readString(norm, config.fieldMappings.image) || undefined
        : undefined;
    const logoImage = config.fieldMappings.logoImage
        ? readString(norm, config.fieldMappings.logoImage) || undefined
        : undefined;
    const href = config.fieldMappings.href
        ? config.fieldMappings.href(recordId)
        : `/item/${encodeURIComponent(recordId)}`;
    return {
        recordId,
        title,
        subtitle,
        icon,
        image: image ? resolveAssetUrl(baseUrl, image) : undefined,
        logoImage: logoImage ? resolveAssetUrl(baseUrl, logoImage) : undefined,
        accentColor,
        href,
    };
}
function gridDataArray(res) {
    const d = res.Data ?? res.data;
    return Array.isArray(d) ? d : [];
}
function gridTotal(res, dataLen) {
    const t = res.Total ?? res.total;
    if (typeof t === 'number' && Number.isFinite(t))
        return t;
    return dataLen;
}
/**
 * Every raw row from `{baseUrl}/api/PublicFlowBuilderRecord/Grid/{gridId}` — pages until `total`
 * is reached or the server returns a partial page. Callers map/filter the rows themselves.
 */
export async function fetchAllGridRows(baseUrl, gridId, pageSize = 100) {
    const root = baseUrl.replace(/\/$/, '');
    const size = Math.min(500, Math.max(1, Math.floor(pageSize)));
    const rows = [];
    let page = 1;
    let total = Infinity;
    while (rows.length < total) {
        const url = `${root}/api/PublicFlowBuilderRecord/Grid/${encodeURIComponent(gridId)}?pageSize=${size}&page=${page}`;
        const json = (await get(url, null, null, null));
        if (!json)
            throw new Error('Grid fetch failed');
        const chunk = gridDataArray(json);
        total = gridTotal(json, chunk.length);
        for (const row of chunk) {
            if (row && typeof row === 'object')
                rows.push(row);
        }
        if (chunk.length < size)
            break;
        page += 1;
        if (page > 500)
            break;
    }
    return rows;
}
/**
 * Generic grid fetcher - fetches paginated data from any Flow Builder grid
 */
export async function fetchGridData(config) {
    const baseUrl = config.baseUrl.replace(/\/$/, '');
    const pageSize = config.pageSize || 100;
    const all = [];
    const seen = new Set();
    let page = 1;
    let total = Infinity;
    while (all.length < total) {
        const url = `${baseUrl}/api/PublicFlowBuilderRecord/Grid/${encodeURIComponent(config.gridId)}?pageSize=${pageSize}&page=${page}`;
        const json = (await get(url, null, null, null));
        if (!json) {
            console.error('[ERROR] Grid fetch failed for URL:', url);
            throw new Error('Grid fetch failed');
        }
        const chunk = gridDataArray(json);
        total = gridTotal(json, chunk.length);
        for (const row of chunk) {
            const item = mapRowToGridItem(row, config, baseUrl);
            if (!item?.recordId)
                continue;
            const recordIdStr = String(item.recordId);
            if (seen.has(recordIdStr))
                continue;
            seen.add(recordIdStr);
            all.push(item);
        }
        if (chunk.length === 0)
            break;
        page += 1;
        if (page > 500)
            break;
    }
    return all;
}
